import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  invoicePreviewDetailsRecipe,
  invoicePreviewLogoRecipe,
  invoicePreviewRecipe,
  invoicePreviewSectionRecipe,
  invoicePreviewTotalsRecipe,
  type InvoicePreviewRecipeProps,
} from '../recipes/invoice.recipe';
import { Surface } from './Card';
import { FieldValue, ValueCell, formatValue } from './DataValue';
import { Divider, Grid, Inline, Stack } from './Layout';
import { KeyValueRow } from './List';
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from './Table';
import { Text } from './Text';

export interface InvoicePreviewItem {
  description: string;
  qty: number;
  rate: number;
}

/**
 * Everything the document shows, as plain values. No hooks, no fetching:
 * the screen resolves its state into this shape and hands it over.
 */
export interface InvoicePreviewData {
  /** Shown as given — pass the placeholder if it has not been assigned yet. */
  invoiceNumber?: string;
  /** Already formatted for display; the screen decides the locale. */
  issuedOn?: string;
  dueDate?: string;
  company?: { name?: string; email?: string; logoUrl?: string };
  billTo?: { name?: string; email?: string; phone?: string; company?: string };
  items: InvoicePreviewItem[];
  subtotal: number;
  /**
   * Resolved to an amount. The label says how it was entered — "Discount
   * (10%)" — so a percentage still reads as one.
   */
  discount?: { label?: React.ReactNode; amount: number } | null;
  tax?: { label?: React.ReactNode; amount: number } | null;
  total: number;
  memo?: string;
  emailMessage?: string;
}

export interface InvoicePreviewProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    InvoicePreviewRecipeProps {
  invoice: InvoicePreviewData;
  /** The document's heading. */
  title?: React.ReactNode;
}

/**
 * An invoice, laid out as the customer will see it.
 *
 *   <InvoicePreview invoice={{ invoiceNumber: 'INV-1042', items, subtotal, total }} />
 *
 * The create screen shows this in a dialog behind the Preview button, and the
 * generator rasterises it into the PDF it emails — `paper` is that second
 * case, where the page has to stay light whatever the app's theme. Both used
 * to draw the document by hand, with the totals in a different order and the
 * dates in a different weight.
 */
export const InvoicePreview = React.forwardRef<
  HTMLDivElement,
  InvoicePreviewProps
>(function InvoicePreview(
  { invoice, title = 'Invoice', inset, paper, className, ...props },
  ref
) {
  const { company, billTo, items, discount, tax } = invoice;
  const details = [
    invoice.invoiceNumber !== undefined
      ? { label: 'Invoice Number', value: invoice.invoiceNumber }
      : null,
    invoice.issuedOn !== undefined
      ? { label: 'Invoice Date', value: invoice.issuedOn }
      : null,
    invoice.dueDate !== undefined
      ? { label: 'Due Date', value: invoice.dueDate }
      : null,
  ].filter((cell): cell is { label: string; value: string } => cell !== null);

  return (
    <div
      ref={ref}
      className={cn(invoicePreviewRecipe({ inset, paper }), className)}
      {...props}
    >
      <Inline gap={6} align="start" justify="between">
        <Text variant="h1">{title}</Text>
        <Stack gap={0} align="end">
          {company?.logoUrl ? (
            <img
              src={company.logoUrl}
              alt="Company Logo"
              className={invoicePreviewLogoRecipe()}
            />
          ) : null}
          <Text variant="body-lg-strong" align="right">
            {company?.name}
          </Text>
          <Text variant="body-sm" tone="muted" align="right">
            {company?.email}
          </Text>
        </Stack>
      </Inline>

      {details.length ? (
        <Grid cols={2} gap={8} className={invoicePreviewDetailsRecipe()}>
          {details.map((cell, index) => (
            <FieldValue
              key={cell.label}
              label={cell.label}
              value={cell.value}
              className={index % 2 ? 'text-right' : undefined}
            />
          ))}
        </Grid>
      ) : null}

      <Stack gap={1}>
        <Text variant="overline" tone="muted">
          Bill To
        </Text>
        <Text variant="body-lg-strong">{billTo?.name}</Text>
        {billTo?.email ? (
          <Text variant="body-sm" tone="muted">
            {billTo.email}
          </Text>
        ) : null}
        {billTo?.phone ? (
          <Text variant="body-sm" tone="muted">
            {billTo.phone}
          </Text>
        ) : null}
        {billTo?.company ? (
          <Text variant="body-sm" tone="muted">
            {billTo.company}
          </Text>
        ) : null}
      </Stack>

      <Table scrollable={false}>
        <TableHeader sticky={false}>
          <TableRow>
            <TableHead>Description</TableHead>
            <TableHead numeric>Qty</TableHead>
            <TableHead numeric>Rate</TableHead>
            <TableHead numeric>Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length ? (
            items.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Text variant="body-sm-strong" as="span">
                    {item.description}
                  </Text>
                </TableCell>
                <TableCell numeric>{item.qty}</TableCell>
                <ValueCell value={item.rate} format="currency" />
                <ValueCell value={item.qty * item.rate} format="currency" />
              </TableRow>
            ))
          ) : (
            <TableEmpty colSpan={4}>No items added</TableEmpty>
          )}
        </TableBody>
      </Table>

      <Inline gap={0} align="start" justify="end">
        <Stack gap={0} className={invoicePreviewTotalsRecipe()}>
          <KeyValueRow
            label="Subtotal"
            value={invoice.subtotal}
            format="currency"
          />
          {discount ? (
            <KeyValueRow
              label={discount.label ?? 'Discount'}
              value={-discount.amount}
              format="currency"
              tone="destructive"
            />
          ) : null}
          {tax ? (
            <KeyValueRow
              label={tax.label ?? 'Tax'}
              value={tax.amount}
              format="currency"
            />
          ) : null}
          <Divider />
          <KeyValueRow
            emphasis
            label={
              <Text variant="body-lg-strong" as="span">
                Total
              </Text>
            }
          >
            <Text variant="body-lg-strong" as="span" numeric>
              {formatValue(invoice.total, 'currency')}
            </Text>
          </KeyValueRow>
        </Stack>
      </Inline>

      {invoice.memo ? (
        <Stack gap={2} className={invoicePreviewSectionRecipe()}>
          <Text variant="overline" tone="muted">
            Notes
          </Text>
          <Text variant="body-sm" tone="muted" className="whitespace-pre-wrap">
            {invoice.memo}
          </Text>
        </Stack>
      ) : null}

      {invoice.emailMessage ? (
        <Stack gap={2} className={invoicePreviewSectionRecipe()}>
          <Text variant="overline" tone="muted">
            Email Message
          </Text>
          <Surface tone="muted" bordered={false} radius="md" padding="md">
            <Text variant="body-sm" className="whitespace-pre-wrap">
              {invoice.emailMessage}
            </Text>
          </Surface>
        </Stack>
      ) : null}
    </div>
  );
});
